<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from '@/plugins/i18n'
import SaiTooltip from '@/components/common/SaiTooltip/index.ce.vue'
import type { DocumentStatusPresentation } from './documentStatus'

let tooltipSequence = 0

const props = defineProps<{
  presentation: DocumentStatusPresentation
}>()

const i18nInstance: any = useI18n()
const { t } = i18nInstance
const trigger = ref<HTMLElement | null>(null)
const showTooltip = ref(false)
const pinned = ref(false)
const tooltipId = `document-status-tooltip-${++tooltipSequence}`
const definition = computed(() => props.presentation.definitionKey ? t(props.presentation.definitionKey) : '')

const open = () => {
  if (definition.value) showTooltip.value = true
}
const close = () => {
  showTooltip.value = false
  pinned.value = false
}
const leave = () => {
  if (!pinned.value) showTooltip.value = false
}
const toggle = () => {
  if (!definition.value) return
  pinned.value = !pinned.value
  showTooltip.value = pinned.value
}
const handleDocumentClick = (event: MouseEvent) => {
  if (pinned.value && !trigger.value?.contains(event.target as Node)) close()
}

onMounted(() => document.addEventListener('click', handleDocumentClick))
onBeforeUnmount(() => document.removeEventListener('click', handleDocumentClick))
</script>

<template>
  <div class="document-status">
    <div
      ref="trigger"
      :class="['doc-details--status-label', 'status-label', presentation.className]"
      :tabindex="definition ? 0 : undefined"
      :role="definition ? 'button' : undefined"
      :aria-describedby="definition ? tooltipId : undefined"
      :aria-expanded="definition ? showTooltip : undefined"
      @mouseenter="open"
      @mouseleave="leave"
      @focus="open"
      @blur="leave"
      @click.stop="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
      @keydown.escape="close"
    >
      {{ t(presentation.labelKey) }}
    </div>
    <SaiTooltip
      v-if="definition && trigger"
      v-model="showTooltip"
      :target="trigger"
      :tooltip-id="tooltipId"
      placement="left-middle"
      :offset="8"
    >
      {{ definition }}
    </SaiTooltip>
  </div>
</template>
